import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Mail, MessageSquare, Phone, StickyNote, Plus, Send, Clock, User, ChevronDown, ChevronUp } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';

const API_BASE = '/api';

function addAuth(instance: ReturnType<typeof axios.create>) {
  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem('grant_crm_token');
    if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  instance.interceptors.response.use((r) => r, (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('grant_crm_token');
      localStorage.removeItem('grant_crm_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  });
  return instance;
}

const authAxios = addAuth(axios.create({ headers: { 'Content-Type': 'application/json' } }));

interface Communication {
  id: number;
  comm_type: 'email' | 'sms' | 'phone' | 'note';
  direction: 'outbound' | 'inbound';
  subject: string | null;
  message_preview: string | null;
  recipient: string | null;
  status: string;
  provider: string | null;
  created_at: string;
  created_by_name: string | null;
}

const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  email: { icon: Mail, color: 'text-blue-600 bg-blue-100', label: 'Email' },
  sms: { icon: MessageSquare, color: 'text-green-600 bg-green-100', label: 'SMS' },
  phone: { icon: Phone, color: 'text-orange-600 bg-orange-100', label: 'Telefon' },
  note: { icon: StickyNote, color: 'text-purple-600 bg-purple-100', label: 'Notatka' },
};

interface Props {
  applicationId: number;
}

export const CommunicationHistoryPanel: React.FC<Props> = ({ applicationId }) => {
  const [comms, setComms] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<'phone' | 'note'>('phone');
  const [formSubject, setFormSubject] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formRecipient, setFormRecipient] = useState('');
  const [formDirection, setFormDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [submitting, setSubmitting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchComms = async () => {
    try {
      const res = await authAxios.get(`${API_BASE}/communications/${applicationId}`);
      if (mountedRef.current) {
        setComms(res.data.communications || []);
      }
    } catch (err) {
      console.error('Failed to load communications:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchComms();
  }, [applicationId]);

  const handleSubmit = async () => {
    if (!formMessage.trim()) return;
    setSubmitting(true);
    try {
      await authAxios.post(`${API_BASE}/communications/${applicationId}/log`, {
        comm_type: formType,
        direction: formDirection,
        subject: formSubject || null,
        message_preview: formMessage,
        recipient: formRecipient || null,
      });
      setFormSubject('');
      setFormMessage('');
      setFormRecipient('');
      setShowForm(false);
      fetchComms();
    } catch (err) {
      console.error('Failed to log communication:', err);
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const displayComms = expanded ? comms : comms.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold flex items-center gap-2">
          <Clock size={18} />
          Historia komunikacji
          {comms.length > 0 && (
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{comms.length}</span>
          )}
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 flex items-center gap-1"
        >
          <Plus size={14} />
          Dodaj wpis
        </button>
      </div>

      {/* Add entry form */}
      {showForm && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border space-y-3">
          <div className="flex gap-2">
            <select
              value={formType}
              onChange={(e) => setFormType(e.target.value as 'phone' | 'note')}
              className="text-sm border rounded-lg px-3 py-2 bg-white"
            >
              <option value="phone">Rozmowa tel.</option>
              <option value="note">Notatka</option>
              <option value="email">Email (ręczny)</option>
              <option value="sms">SMS (ręczny)</option>
            </select>
            <select
              value={formDirection}
              onChange={(e) => setFormDirection(e.target.value as 'outbound' | 'inbound')}
              className="text-sm border rounded-lg px-3 py-2 bg-white"
            >
              <option value="outbound">Wychodzące</option>
              <option value="inbound">Przychodzące</option>
            </select>
          </div>
          <input
            type="text"
            value={formRecipient}
            onChange={(e) => setFormRecipient(e.target.value)}
            placeholder="Numer tel. / email odbiorcy"
            className="w-full text-sm border rounded-lg px-3 py-2"
          />
          <input
            type="text"
            value={formSubject}
            onChange={(e) => setFormSubject(e.target.value)}
            placeholder="Temat (opcjonalnie)"
            className="w-full text-sm border rounded-lg px-3 py-2"
          />
          <RichTextEditor
            value={formMessage}
            onChange={setFormMessage}
            placeholder="Opis rozmowy / tresc notatki..."
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowForm(false)}
              className="text-sm text-gray-600 px-4 py-2 hover:bg-gray-200 rounded-lg"
            >
              Anuluj
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !formMessage.trim()}
              className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-1"
            >
              <Send size={14} />
              {submitting ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <p className="text-sm text-gray-500">Ładowanie...</p>
      ) : comms.length === 0 ? (
        <p className="text-sm text-gray-400 italic">Brak wpisów komunikacji</p>
      ) : (
        <div className="space-y-3">
          {displayComms.map((c) => {
            const cfg = TYPE_CONFIG[c.comm_type] || TYPE_CONFIG.note;
            const Icon = cfg.icon;
            return (
              <div key={c.id} className="flex gap-3 items-start">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{cfg.label}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${c.direction === 'inbound' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>
                      {c.direction === 'inbound' ? 'przychodzące' : 'wychodzące'}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${c.status === 'sent' || c.status === 'logged' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {c.status}
                    </span>
                  </div>
                  {c.subject && (
                    <p className="text-sm text-gray-700 font-medium truncate">{c.subject}</p>
                  )}
                  {c.message_preview && (
                    <p className="text-xs text-gray-500 line-clamp-2">{c.message_preview.replace(/<[^>]*>/g, '').substring(0, 200)}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span>{new Date(c.created_at).toLocaleString('pl-PL')}</span>
                    {c.recipient && <span>→ {c.recipient}</span>}
                    {c.created_by_name && (
                      <span className="flex items-center gap-1"><User size={10} />{c.created_by_name}</span>
                    )}
                    {c.provider && <span className="italic">{c.provider}</span>}
                  </div>
                </div>
              </div>
            );
          })}

          {comms.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full text-sm text-blue-600 hover:text-blue-700 py-2 flex items-center justify-center gap-1"
            >
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              {expanded ? 'Pokaż mniej' : `Pokaż wszystkie (${comms.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
